import React, { useContext, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartSimple,
  faShield,
  faChartLine,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import { ThemeContext } from "../../../context/ThemeContext";
import "../../../pages/Landing/LandingPage.css";

const Services = () => {
  const { darkMode } = useContext(ThemeContext);
  const featureRefs = useRef([]);

  // Reset feature refs array when component mounts
  useEffect(() => {
    featureRefs.current = featureRefs.current.slice(0, 4);
  }, []);

  // Handle mouse movement for 3D card effect
  const handleMouseMove = (e, index) => {
    const card = featureRefs.current[index];
    if (!card) return;

    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left; // x position within the element
    const y = e.clientY - rect.top; // y position within the element

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    // Calculate tilt values with a maximum tilt of 15 degrees
    const maxTilt = 15;
    const tiltX = ((y - centerY) / centerY) * -maxTilt;
    const tiltY = ((x - centerX) / centerX) * maxTilt;

    // Calculate percentage position for shine effect (0-100%)
    const percentX = Math.max(0, Math.min(100, (x / rect.width) * 100));
    const percentY = Math.max(0, Math.min(100, (y / rect.height) * 100));

    // Apply the tilt effect with more natural easing
    requestAnimationFrame(() => {
      card.style.transform = `perspective(1000px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) translateZ(20px) scale(1.03)`;

      // Position the shine effect to follow the cursor
      const shine = card.querySelector(".shine-effect");
      if (shine) {
        shine.style.opacity = "1";
        shine.style.background = `radial-gradient(circle at ${percentX}% ${percentY}%, rgba(255, 255, 255, 0.35) 0%, rgba(255, 255, 255, 0) 60%)`;
      }
    });
  };

  // Reset card position when mouse leaves
  const handleMouseLeave = (index) => {
    const card = featureRefs.current[index];
    if (!card) return;

    // Reset the transform with a smooth transition
    requestAnimationFrame(() => {
      card.style.transform =
        "perspective(1000px) rotateX(0deg) rotateY(0deg) translateZ(0px) scale(1)";

      // Reset shine effect
      const shine = card.querySelector(".shine-effect");
      if (shine) {
        shine.style.opacity = "0";
      }
    });
  };

  return (
    <section
      className={`features-section py-5 ${darkMode ? "dark-mode" : ""}`}
      id="services"
      style={{
        backgroundColor: darkMode ? "#1e1e1e" : "#00d389",
        borderRadius: "20px",
      }}
    >
      <div className="container">
        <div className="text-center mb-5">
          <h2
            className="mb-3 services-main-title"
            style={{ color: "#fff", fontSize: "40px", fontWeight: "700" }}
          >
            Our Services
          </h2>
          <p
            className="services-subtitle"
            style={{
              color: "rgba(255, 255, 255, 0.9)",
              maxWidth: "700px",
              margin: "0 auto",
            }}
          >
            We provide comprehensive accounting solutions tailored to your
            business needs
          </p>
        </div>
        <div className="feature-bar">
          <div className="feature-row">
            <div className="feature-col col-6 col-lg-3 col-md-6">
              <div
                className={`feature ${darkMode ? "dark-feature" : ""}`}
                ref={(el) => (featureRefs.current[0] = el)}
                onMouseMove={(e) => handleMouseMove(e, 0)}
                onMouseLeave={() => handleMouseLeave(0)}
              >
                <div className="shine-effect"></div>
                <FontAwesomeIcon
                  icon={faChartSimple}
                  className="feature-icons"
                  size="2x"
                  style={{ color: darkMode ? "#00d389" : "#ffffff" }}
                />
                <h3
                  className="feature-title"
                  style={{ color: darkMode ? "#f5f5f5" : "#ffffff" }}
                >
                  Complete Sales Cycle
                </h3>
                <p
                  className="feature-text"
                  style={{ color: darkMode ? "#cccccc" : "#ffffff" }}
                >
                  Manage quotes, orders, invoices and credit notes in a single
                  unified platform
                </p>
              </div>
            </div>
            <div className="feature-col col-6 col-lg-3 col-md-6">
              <div
                className={`feature ${darkMode ? "dark-feature" : ""}`}
                ref={(el) => (featureRefs.current[1] = el)}
                onMouseMove={(e) => handleMouseMove(e, 1)}
                onMouseLeave={() => handleMouseLeave(1)}
              >
                <div className="shine-effect"></div>
                <FontAwesomeIcon
                  icon={faShield}
                  className="feature-icons"
                  size="2x"
                  style={{ color: darkMode ? "#00d389" : "#ffffff" }}
                />
                <h3
                  className="feature-title"
                  style={{ color: darkMode ? "#f5f5f5" : "#ffffff" }}
                >
                  Purchase Management
                </h3>
                <p
                  className="feature-text"
                  style={{ color: darkMode ? "#cccccc" : "#ffffff" }}
                >
                  Track requisitions, vendor quotes, purchase orders, bills and
                  returns
                </p>
              </div>
            </div>
            <div className="feature-col col-6 col-lg-3 col-md-6">
              <div
                className={`feature ${darkMode ? "dark-feature" : ""}`}
                ref={(el) => (featureRefs.current[2] = el)}
                onMouseMove={(e) => handleMouseMove(e, 2)}
                onMouseLeave={() => handleMouseLeave(2)}
              >
                <div className="shine-effect"></div>
                <FontAwesomeIcon
                  icon={faChartLine}
                  className="feature-icons"
                  size="2x"
                  style={{ color: darkMode ? "#00d389" : "#ffffff" }}
                />
                <h3
                  className="feature-title"
                  style={{ color: darkMode ? "#f5f5f5" : "#ffffff" }}
                >
                  Financial Reporting
                </h3>
                <p
                  className="feature-text"
                  style={{ color: darkMode ? "#cccccc" : "#ffffff" }}
                >
                  Generate profit & loss statements, balance sheets, tax reports
                  and aging reports
                </p>
              </div>
            </div>
            <div className="feature-col col-6 col-lg-3 col-md-6">
              <div
                className={`feature ${darkMode ? "dark-feature" : ""}`}
                ref={(el) => (featureRefs.current[3] = el)}
                onMouseMove={(e) => handleMouseMove(e, 3)}
                onMouseLeave={() => handleMouseLeave(3)}
              >
                <div className="shine-effect"></div>
                <FontAwesomeIcon
                  icon={faClock}
                  className="feature-icons"
                  size="2x"
                  style={{ color: darkMode ? "#00d389" : "#ffffff" }}
                />
                <h3
                  className="feature-title"
                  style={{ color: darkMode ? "#f5f5f5" : "#ffffff" }}
                >
                  Inventory Management
                </h3>
                <p
                  className="feature-text"
                  style={{ color: darkMode ? "#cccccc" : "#ffffff" }}
                >
                  Track products, categories and maintain complete control over
                  your inventory
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .features-section {
          padding: 80px 0;
          background-color: ${darkMode ? "#1e1e1e" : "#00d389"};
          border-radius: 20px;
          margin: -10px 0;
        }

        .services-main-title {
          color: #fff !important;
          font-size: 40px !important;
          font-weight: 700 !important;
        }

        .services-subtitle {
          color: rgba(255, 255, 255, 0.9) !important;
          max-width: 700px !important;
          margin: 0 auto !important;
        }

        .feature-bar {
          width: 100%;
        }

        .feature-row {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
          justify-content: center;
        }

        .feature-col {
          flex: 1;
          min-width: 250px;
          max-width: 300px;
        }

        .feature {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border-radius: 15px;
          padding: 30px 20px;
          text-align: center;
          position: relative;
          overflow: hidden;
          transition: all 0.3s ease;
          border: 1px solid rgba(255, 255, 255, 0.2);
          height: 280px;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .feature:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
        }

        .shine-effect {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.35) 0%, rgba(255, 255, 255, 0) 60%);
          opacity: 0;
          transition: opacity 0.3s ease;
          pointer-events: none;
        }

        .feature-icons {
          font-size: 40px;
          margin-bottom: 20px;
          transition: all 0.3s ease;
        }

        .feature-title {
          font-size: 20px;
          font-weight: 600;
          margin-bottom: 15px;
          transition: all 0.3s ease;
        }

        .feature-text {
          font-size: 14px;
          line-height: 1.6;
          opacity: 0.9;
          transition: all 0.3s ease;
        }

        .dark-feature {
          background: rgba(45, 45, 45, 0.9);
          border: 1px solid rgba(0, 211, 137, 0.3);
        }

        /* Mobile-specific optimizations */
        @media (max-width: 767.98px) {
          .features-section {
            padding: 40px 0;
            margin: 15px 0;
          }

          .services-main-title {
            font-size: 32px !important;
          }

          .services-subtitle {
            font-size: 16px !important;
          }

          .feature-row {
            gap: 15px;
          }

          .feature-col {
            min-width: calc(50% - 7.5px);
            max-width: calc(50% - 7.5px);
            flex: 0 0 calc(50% - 7.5px);
          }

          .feature {
            height: 240px;
            padding: 20px 15px;
          }

          .feature-icons {
            font-size: 32px;
            margin-bottom: 15px;
          }

          .feature-title {
            font-size: 16px;
            margin-bottom: 12px;
          }

          .feature-text {
            font-size: 12px;
            line-height: 1.5;
          }
        }

        /* Small mobile devices (sm breakpoint) */
        @media (max-width: 575.98px) {
          .services-main-title {
            font-size: 28px !important;
          }

          .services-subtitle {
            font-size: 14px !important;
          }

          .feature-col {
            min-width: calc(50% - 7.5px);
            max-width: calc(50% - 7.5px);
            flex: 0 0 calc(50% - 7.5px);
          }

          .feature {
            height: 220px;
            padding: 18px 12px;
          }

          .feature-icons {
            font-size: 28px;
            margin-bottom: 12px;
          }

          .feature-title {
            font-size: 14px;
            margin-bottom: 10px;
          }

          .feature-text {
            font-size: 11px;
            line-height: 1.4;
          }
        }

        /* Extra small devices */
        @media (max-width: 375px) {
          .services-main-title {
            font-size: 24px !important;
          }

          .services-subtitle {
            font-size: 13px !important;
          }

          .feature-col {
            min-width: 100%;
            max-width: 100%;
            flex: 0 0 100%;
          }

          .feature {
            height: 200px;
            padding: 16px 12px;
          }

          .feature-icons {
            font-size: 24px;
            margin-bottom: 10px;
          }

          .feature-title {
            font-size: 13px;
            margin-bottom: 8px;
          }

          .feature-text {
            font-size: 10px;
            line-height: 1.3;
          }
        }
      `}</style>
    </section>
  );
};

export default Services;


